// Staff, station PINs and roles (plan §10.2, §10.3; backlog B-080). Wire format: snake_case
// fields, instants as ISO-8601 UTC strings.
//
// Admin (`staff.manage`, within the caller's branch scope):
//   GET   /staff?status=active|archived   people, the roles they hold and where, stations
//   POST  /staff                          add someone (idempotency-key header)
//   PATCH /staff/:id                      rename, archive, restore
//   PUT   /staff/:id/roles                the roles they hold, each for one branch or all
//   POST  /staff/:id/pin                  set or reset their station PIN (rate-limited)
//   POST  /devices/:id/unlock-pin         unlock a station's PIN pad now (D-047)
//   GET   /roles                          roles and their permission keys
//   POST  /roles                          add a role (all branches; idempotency-key header)
//   PATCH /roles/:id                      rename, change permissions, archive (all branches)
//
// A staff member is a name and a PIN: nothing else about the person is stored (POPIA-minimal).
// The PIN is hashed on the server exactly as the station checks it (@enp/domain `hashPin`,
// scrypt) and is never stored or returned in clear; the hash syncs to the station, so the old
// PIN stops working there on its next sync.
import { z } from 'zod';
import { permissionKeySchema } from '../permissions/index.js';

const instant = z.iso.datetime({ offset: true });
const count = z.int().min(0);

/** Longest staff display name: what fits on a station's name tile. */
export const STAFF_NAME_MAX = 40;
/** Longest role name. */
export const ROLE_NAME_MAX = 40;
/** Most role assignments one person holds. */
export const STAFF_ROLES_MAX = 20;

/** `:id` path parameter of the staff and role routes. */
export const staffIdParamsSchema = z.object({ id: z.uuid() });

const displayName = z.string().trim().min(1).max(STAFF_NAME_MAX);
const roleName = z.string().trim().min(1).max(ROLE_NAME_MAX);

/**
 * True for a PIN anyone would try first: one digit repeated (`1111`) or a straight run up or
 * down (`1234`, `9876`). Refused when a manager sets a PIN; the Admin never suggests one.
 */
export function isEasyPin(pin: string): boolean {
  if (!/^\d{4,6}$/.test(pin)) return false;
  const d = [...pin].map(Number);
  const steps = new Set(d.slice(1).map((x, i) => x - (d[i] as number)));
  return steps.size === 1 && [0, 1, -1].includes([...steps][0] as number);
}

/** A station PIN: 4–6 digits (as the station accepts), and not an obvious one. */
export const staffPinSchema = z
  .string()
  .regex(/^\d{4,6}$/, 'A PIN is 4 to 6 digits')
  .refine((p) => !isEasyPin(p), 'Too easy to guess: avoid repeats and runs like 1234');

// ---------------------------------------------------------------------------------------
// Staff

/** A role given to someone, for one branch or for all (`branch_id: null`). */
export const staffRoleAssignmentSchema = z.object({
  role_id: z.uuid(),
  branch_id: z.uuid().nullable(),
});
export type StaffRoleAssignment = z.infer<typeof staffRoleAssignmentSchema>;

/** A role someone holds, as the Admin lists it. */
export const staffRoleSchema = staffRoleAssignmentSchema.extend({
  role_name: z.string(),
});
export type StaffRole = z.infer<typeof staffRoleSchema>;

/**
 * Why the caller can't change someone: they also work in another branch, or their roles
 * allow something the caller's don't (CR8A-1).
 */
export const staffCannotManageReasonSchema = z.enum(['other_branch', 'more_access']);
export type StaffCannotManageReason = z.infer<typeof staffCannotManageReasonSchema>;

/** A member of staff: someone who signs in at a station with a PIN. */
export const staffMemberSchema = z.object({
  id: z.uuid(),
  display_name: z.string(),
  /** Whether they have a PIN (without one they can't sign in at a station). */
  has_pin: z.boolean(),
  pin_updated_at: instant.nullable(),
  roles: z.array(staffRoleSchema),
  created_at: instant,
  archived_at: instant.nullable(),
  /**
   * False when the caller can't change them: a manager of one branch and someone who also
   * works elsewhere (or everywhere), or someone whose roles allow more than the caller's.
   */
  can_manage: z.boolean(),
  /** Why `can_manage` is false (null when it is true). */
  cannot_manage_reason: staffCannotManageReasonSchema.nullable(),
});
export type StaffMember = z.infer<typeof staffMemberSchema>;

/** A device whose PIN pad a manager can unlock (D-047). */
export const pinPadStationSchema = z.object({
  device_id: z.uuid(),
  device_code: z.string(),
  station_id: z.uuid(),
  station_name: z.string(),
  branch_id: z.uuid(),
  /** When a manager last unlocked it from the Admin. */
  unlock_requested_at: instant.nullable(),
});
export type PinPadStation = z.infer<typeof pinPadStationSchema>;

/** `GET /staff` query. */
export const staffListQuerySchema = z.object({
  status: z.enum(['active', 'archived']).default('active'),
});

/** `GET /staff` response. */
export const staffListResponseSchema = z.object({
  /** The branches the caller can give roles in (theirs only, when limited to one). */
  branches: z.array(z.object({ id: z.uuid(), name: z.string() })),
  staff: z.array(staffMemberSchema),
  /** People archived (shown under their own filter). */
  archived_count: count,
  /** Active devices in the caller's branches, for unlocking a PIN pad. */
  stations: z.array(pinPadStationSchema),
  /** Whether the caller may give roles for all branches. */
  all_branches: z.boolean(),
});
export type StaffListResponse = z.infer<typeof staffListResponseSchema>;

const roles = z.array(staffRoleAssignmentSchema).max(STAFF_ROLES_MAX);

/** `POST /staff`: a name and their roles. The PIN is set next, on its own route. */
export const staffCreateRequestSchema = z.object({
  display_name: displayName,
  roles,
});
export type StaffCreateRequest = z.infer<typeof staffCreateRequestSchema>;

/** `PATCH /staff/:id`: rename, archive (`true`) or restore (`false`). */
export const staffUpdateRequestSchema = z
  .object({ display_name: displayName.optional(), archived: z.boolean().optional() })
  .refine((b) => b.display_name !== undefined || b.archived !== undefined, 'Nothing to change');
export type StaffUpdateRequest = z.infer<typeof staffUpdateRequestSchema>;

/** `PUT /staff/:id/roles`: every role they should hold (replaces the current set). */
export const staffRolesRequestSchema = z.object({ roles });
export type StaffRolesRequest = z.infer<typeof staffRolesRequestSchema>;

/** `POST /staff/:id/pin`. Answered with the member (never the PIN or its hash). */
export const staffPinRequestSchema = z.object({ pin: staffPinSchema });
export type StaffPinRequest = z.infer<typeof staffPinRequestSchema>;

/** `POST /devices/:id/unlock-pin` response. */
export const pinUnlockResponseSchema = pinPadStationSchema;
export type PinUnlockResponse = z.infer<typeof pinUnlockResponseSchema>;

// ---------------------------------------------------------------------------------------
// Roles

/** A named set of permission keys (plan §10.3). */
export const roleSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  /** What a default role is for; null for the account's own roles. */
  description: z.string().nullable(),
  /** Seeded default (Owner, Manager, …): editable, never deleted. */
  is_default: z.boolean(),
  permissions: z.array(permissionKeySchema),
  /** Active staff holding it (anywhere). */
  staff_count: count,
  /** Admin members whose role it is. */
  member_count: count,
});
export type Role = z.infer<typeof roleSchema>;

/** `GET /roles` response. */
export const rolesResponseSchema = z.object({ roles: z.array(roleSchema) });
export type RolesResponse = z.infer<typeof rolesResponseSchema>;

const permissions = z
  .array(permissionKeySchema)
  .refine((p) => new Set(p).size === p.length, 'Each permission once');

/** `POST /roles`. */
export const roleCreateRequestSchema = z.object({ name: roleName, permissions });
export type RoleCreateRequest = z.infer<typeof roleCreateRequestSchema>;

/** `PATCH /roles/:id`: rename, set its permissions, or archive it (not a default role). */
export const roleUpdateRequestSchema = z
  .object({
    name: roleName.optional(),
    permissions: permissions.optional(),
    archived: z.literal(true).optional(),
  })
  .refine(
    (b) => b.name !== undefined || b.permissions !== undefined || b.archived !== undefined,
    'Nothing to change',
  );
export type RoleUpdateRequest = z.infer<typeof roleUpdateRequestSchema>;
